import type { KeyboardEvent } from 'react';
import { Icon, type IconName } from '../shell/Icon.tsx';
import styles from './Tabs.module.css';

/* ==========================================================================
   Tabs — verb tabs dividing one object's surfaces, or workspace tabs holding
   saved layouts. Both are flat and underline-marked. Left/Right arrow keys
   move between them; Home/End jump to the ends.
   ========================================================================== */

export interface TabItem {
  id: string;
  label: string;
  /**
   * `number | string` so a tab count can be `—`. A ZERO IS A MEASUREMENT AND
   * AN UNREAD SOURCE IS NOT: `Telemetry 0` on a robot whose telemetry read
   * failed is the tab strip inventing a census, so the caller passes
   * `figure(read, rows.length)` (components/states/read.ts) and the strip
   * prints whichever of the two it got.
   */
  count?: number | string | undefined;
  icon?: IconName | undefined;
  disabled?: boolean | undefined;
}

export function Tabs({
  items,
  active,
  onChange,
  flush = false,
  label = 'Sections',
}: {
  items: readonly TabItem[];
  active: string;
  onChange: (id: string) => void;
  flush?: boolean | undefined;
  label?: string | undefined;
}) {
  const move = (e: KeyboardEvent<HTMLDivElement>) => {
    const usable = items.filter((i) => i.disabled !== true);
    const idx = usable.findIndex((i) => i.id === active);
    if (idx < 0) return;
    let next = idx;
    if (e.key === 'ArrowRight') next = (idx + 1) % usable.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + usable.length) % usable.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = usable.length - 1;
    else return;
    e.preventDefault();
    const target = usable[next];
    if (target) onChange(target.id);
  };

  return (
    <div
      className={styles.strip}
      data-flush={flush}
      role="tablist"
      aria-label={label}
      onKeyDown={move}
    >
      {items.map((t) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={selected}
            aria-controls={`tabpanel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            disabled={t.disabled}
            className={styles.tab}
            onClick={() => onChange(t.id)}
          >
            {t.icon !== undefined && <Icon name={t.icon} size={14} />}
            {t.label}
            {t.count !== undefined && <span className={styles.count}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
